///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { GtthAbstractCommand } from '../../abstract-command';
import { SectorFeature } from '../../../entities/sector/sector-feature';
import { LinkFeature } from '../../../entities/link/link-feature';
import { ActivityDTO } from '../../../../../services/project-socket/activity-dto.model';
import { GtthLinkFeatureDtoModel } from '../../../../../services/project-socket/dto/gtth-link-feature-dto.model';
import { isDefined } from '@siklu/sui-lib/utils';

export class GtthChangeFrequenciesMultipleLinksCommand extends GtthAbstractCommand {
    private static readonly FDD_FREQUENCIES_DELTA = 10000;

    private linkIds: number[];
    private linkSector: SectorFeature;
    private newLinkFrequency: number;
    private newFrequencyForced: boolean;
    private oldLinkFrequency: number;
    private oldFrequencyForced: boolean;
    private linkProdFx: boolean;
    private frequencyChanged = false;

    getCommandName(): string {
        return 'GtthChangeFrequenciesMultipleLinksCommand';
    }

    create(oldFrequency: number, oldFrequencyForced: boolean, linkIds: number[], linkSector?: SectorFeature): this {
        this.oldLinkFrequency = oldFrequency;
        this.oldFrequencyForced = oldFrequencyForced;
        this.linkIds = linkIds;
        this.linkSector = linkSector;
        return this;
    }

    setNewLinkFrequency(newFrequency: number, newFrequencyForced: boolean, isFxProductType: boolean): void {
        this.newLinkFrequency = newFrequency;
        this.newFrequencyForced = newFrequencyForced;
        this.linkProdFx = isFxProductType;
    }

    doCommand(): void {
        if (
            !isDefined(this.newLinkFrequency) ||
            !isDefined(this.newFrequencyForced) ||
            (this.newLinkFrequency === this.oldLinkFrequency && this.newFrequencyForced === this.oldFrequencyForced)
        ) {
            this.commandResolve(false);
            return;
        }

        this.frequencyChanged = this.newLinkFrequency !== this.oldLinkFrequency || this.newFrequencyForced !== this.oldFrequencyForced;

        const activity = this.createActivityDTO(false);
        activity.links = this.linkIds.map((linkId) => {
            const link: LinkFeature = this.historyService.logicalTopology.getLink(linkId);
            this.setLinkFrequencies(this.newLinkFrequency, this.newFrequencyForced, link);
            return link.toGeoJson();
        });

        this.sendActivity(activity);
    }

    undoCommand() {
        const activity = this.createActivityDTO(false);
        activity.links = [];

        this.linkIds.forEach((linkId: number) => {
            const link: LinkFeature = this.historyService.logicalTopology.getLink(linkId);
            this.setLinkFrequencies(this.oldLinkFrequency, this.oldFrequencyForced, link);
            activity.links.push(link.toGeoJson());
        });
        this.sendActivity(activity);
    }

    doCommandActivity(activityDto: ActivityDTO) {
        this.applyChanges(activityDto);
    }

    undoCommandActivity(activityDto: ActivityDTO) {
        this.applyChanges(activityDto);
    }

    isDisabledInReadOnlyMode(): boolean {
        return true;
    }

    isModifyTopology(): boolean {
        return true;
    }

    private setLinkFrequencies(frequency: number, frequencyForced: boolean, link: LinkFeature): void {
        link.setFrequencyForced(frequencyForced);
        // if forced - freeze links, do nothing otherwise
        if (frequencyForced) {
            link.setUserForced(true);
        }

        // if device uses Time-division duplex - set same frequency for both TX and RX
        if (!this.linkProdFx) {
            link.setTxFrequency(frequency);
            link.setRxFrequency(frequency);
            this.linkSector?.setFrequency(frequency);
            return;
        }

        // otherwise - device uses Frequency-division duplex - use different values for TX and RX
        // for device with higher frequency set new higher frequency
        if (link.getTxFrequency() > link.getRxFrequency()) {
            link.setTxFrequency(frequency + GtthChangeFrequenciesMultipleLinksCommand.FDD_FREQUENCIES_DELTA);
            link.setRxFrequency(frequency);
        } else {
            link.setTxFrequency(frequency);
            link.setRxFrequency(frequency + GtthChangeFrequenciesMultipleLinksCommand.FDD_FREQUENCIES_DELTA);
        }
    }

    private applyChanges(activityDto: ActivityDTO): void {
        activityDto.links.forEach((activityLink: GtthLinkFeatureDtoModel) => {
            const link = this.historyService.logicalTopology.getLink(activityLink.getId());
            link.parseGeoJson(activityLink);
            this.historyService.logicalTopology.updateLinkSector(link);
        });

        if (this.commandResolve) {
            this.commandResolve(activityDto.successful);
        }
    }
}
